import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import Nav from "@/components/Nav";
import Box3D from "@/components/Box3D";
import {
  ArrowLeft, Download, ImageSquare, CubeFocus, StackSimple,
  FileArrowDown, FileImage, FilePdf,
} from "@phosphor-icons/react";

const inputCls =
  "w-full px-3 py-2 rounded-md border border-[var(--pf-border)] bg-white text-sm focus:outline-none focus:border-[var(--pf-brand)]";

function Slider({ label, unit, value, onChange, min, max, step = 1, decimals = 0, testid }) {
  const fmt = (v) => decimals ? Number(v).toFixed(decimals) : Math.round(v);
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <label className="text-xs font-bold uppercase tracking-widest text-[var(--pf-text-2)]">
          {label}
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={min}
            max={max}
            value={fmt(value)}
            step={step}
            data-testid={`${testid}-number`}
            onChange={e => onChange(Math.max(min, Math.min(max, Number(e.target.value) || min)))}
            className="w-20 text-right text-sm font-bold border-b border-[var(--pf-border)] focus:outline-none focus:border-[var(--pf-brand)]"
          />
          <span className="text-xs text-[var(--pf-text-2)] w-6">{unit}</span>
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        step={step}
        data-testid={`${testid}-range`}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full accent-[var(--pf-brand)]"
      />
      <div className="flex justify-between text-[10px] text-[var(--pf-text-3)] mt-0.5">
        <span>{fmt(min)}</span><span>{fmt(max)}</span>
      </div>
    </div>
  );
}

export default function DielineConfigurator() {
  const { slug } = useParams();
  const [template, setTemplate] = useState(null);
  const [dims, setDims] = useState({ L: 0, W: 0, H: 0, t: 0.5, glue: 15 });
  const [preview, setPreview] = useState(null);   // { svg, geometry, width_mm, height_mm, dimensions }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("flat");    // 'flat' | '3d'
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const debounceRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load template metadata
  useEffect(() => {
    document.title = "Loading… | Talaio Dielines";
    api.get(`/dielines/templates/${slug}`).then(({ data }) => {
      setTemplate(data);
      setDims({
        L: data.defaults.L, W: data.defaults.W, H: data.defaults.H,
        t: 0.5, glue: 15,
      });
      document.title = `${data.name} — Free Die-Cut Template | Talaio/AI`;
    }).catch(() => setErr("Template not found."));
  }, [slug]);

  // Debounced preview
  useEffect(() => {
    if (!template) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setBusy(true);
        const { data } = await api.post(`/dielines/${slug}/preview`, {
          length: dims.L, width: dims.W, height: dims.H,
          material_thickness: dims.t, glue_flap: dims.glue,
        });
        setPreview(data);
        setErr(null);
      } catch (e) {
        setErr(e.response?.data?.detail || "Preview failed");
      } finally {
        setBusy(false);
      }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [dims, slug, template]);

  const downloadUrl = (fmt) => {
    const q = new URLSearchParams({
      L: dims.L, W: dims.W, H: dims.H, t: dims.t, glue: dims.glue,
    }).toString();
    return `${API}/dielines/${slug}/download.${fmt}?${q}`;
  };

  const onLogoPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) {
      toast.error("Logo too large — please upload a file under 4MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setLogoDataUrl(reader.result);
    reader.readAsDataURL(f);
  };

  // Compute the SVG for the flat preview, with logo overlaid on the front panel if provided
  const flatSvgWithLogo = useMemo(() => {
    if (!preview?.svg) return null;
    if (!logoDataUrl) return preview.svg;

    const frontFace = preview.geometry?.faces?.find(f => f.box_face === "front");
    if (!frontFace) return preview.svg;

    // Inject an <image> tag positioned over the front panel of the dieline
    // (SVG uses viewBox in mm; margin is 8mm by default)
    const margin = 8;
    const pad = Math.min(frontFace.w, frontFace.h) * 0.15;
    const imgW = frontFace.w - 2 * pad;
    const imgH = frontFace.h - 2 * pad;
    const x = frontFace.x + margin + pad;
    const y = frontFace.y + margin + pad;
    const imgTag = `<image href="${logoDataUrl}" x="${x}" y="${y}" width="${imgW}" height="${imgH}" preserveAspectRatio="xMidYMid meet" opacity="0.85"/>`;
    return preview.svg.replace("</svg>", `${imgTag}</svg>`);
  }, [preview, logoDataUrl]);

  if (err && !template) {
    return (
      <div className="min-h-screen bg-[var(--pf-bg)]">
        <Nav />
        <div className="max-w-2xl mx-auto p-12 text-center">
          <h1 className="text-2xl font-black mb-2">Template not found</h1>
          <p className="text-[var(--pf-text-2)] mb-6">The dieline "{slug}" doesn't exist.</p>
          <Link to="/dielines" className="pf-btn-primary">Back to templates</Link>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-screen bg-[var(--pf-bg)]">
        <Nav />
        <div className="p-12 pf-label">Loading template…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-8">
        <Link to="/dielines" className="inline-flex items-center gap-1.5 text-sm text-[var(--pf-text-2)] hover:text-[var(--pf-brand)] mb-6" data-testid="back-to-gallery">
          <ArrowLeft size={16} /> All templates
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8">
          {/* --- LEFT: controls --------------------------------------------- */}
          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <div>
              <div className="text-xs pf-label mb-2">{template.category}</div>
              <h1 className="text-2xl md:text-3xl font-black tracking-tight leading-tight mb-2">
                {template.name}
              </h1>
              <p className="text-sm text-[var(--pf-text-2)] leading-relaxed">
                {template.description}
              </p>
            </div>

            <div className="border border-[var(--pf-border)] rounded-lg p-5 bg-white space-y-4">
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--pf-brand)]">
                Dimensions
              </div>
              <Slider label="Length" unit="mm" value={dims.L}
                onChange={v => setDims({ ...dims, L: v })}
                min={template.min.L} max={template.max.L} testid="dim-L"/>
              <Slider label="Width" unit="mm" value={dims.W}
                onChange={v => setDims({ ...dims, W: v })}
                min={template.min.W} max={template.max.W} testid="dim-W"/>
              <Slider label="Height" unit="mm" value={dims.H}
                onChange={v => setDims({ ...dims, H: v })}
                min={template.min.H} max={template.max.H} testid="dim-H"/>
              <div className="pt-3 border-t border-dashed border-[var(--pf-border)] space-y-4">
                <div className="text-xs font-bold uppercase tracking-widest text-[var(--pf-text-2)]">
                  Advanced
                </div>
                <Slider label="Material Thickness" unit="mm" value={dims.t}
                  onChange={v => setDims({ ...dims, t: v })}
                  min={0.1} max={5.0} step={0.1} decimals={1} testid="dim-t"/>
                <Slider label="Glue Flap" unit="mm" value={dims.glue}
                  onChange={v => setDims({ ...dims, glue: v })}
                  min={5} max={40} testid="dim-glue"/>
              </div>
            </div>

            {/* Logo picker */}
            <div className="border border-[var(--pf-border)] rounded-lg p-5 bg-white">
              <div className="flex items-center gap-2 mb-2">
                <ImageSquare size={18} weight="duotone" className="text-[var(--pf-brand)]"/>
                <div className="text-xs font-bold uppercase tracking-widest">Preview your logo on the box</div>
              </div>
              <p className="text-xs text-[var(--pf-text-2)] mb-3">
                Drop a PNG or SVG (transparent bg works best). Only shown in the preview — not baked into the download.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={onLogoPick}
                className="hidden"
                data-testid="logo-picker"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="pf-btn-secondary flex-1 text-sm"
                  data-testid="logo-pick-btn"
                >
                  {logoDataUrl ? "Change logo" : "Upload logo"}
                </button>
                {logoDataUrl && (
                  <button
                    onClick={() => setLogoDataUrl(null)}
                    className="px-3 py-2 text-sm border border-[var(--pf-border)] rounded hover:bg-[var(--pf-surface-2)]"
                    data-testid="logo-clear"
                  >Clear</button>
                )}
              </div>
              {logoDataUrl && (
                <div className="mt-3 p-2 border border-dashed border-[var(--pf-border)] rounded flex items-center justify-center bg-[var(--pf-surface-2)] h-16">
                  <img src={logoDataUrl} alt="Uploaded logo" className="max-h-full max-w-full object-contain" data-testid="logo-thumb"/>
                </div>
              )}
            </div>

            {/* Downloads */}
            <div className="border border-[var(--pf-border)] rounded-lg p-5 bg-white space-y-2">
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--pf-brand)] mb-3">
                Downloads
              </div>
              <a href={downloadUrl("svg")} target="_blank" rel="noreferrer" download
                 data-testid="dl-svg"
                 className="flex items-center gap-3 px-3 py-2.5 rounded border border-[var(--pf-border)] hover:border-[var(--pf-brand)] hover:bg-[var(--pf-surface-2)] transition-colors">
                <FileImage size={20} weight="duotone" className="text-[var(--pf-brand)]"/>
                <div className="flex-1">
                  <div className="font-bold text-sm">SVG</div>
                  <div className="text-[10px] text-[var(--pf-text-2)]">Scalable — best for Illustrator</div>
                </div>
                <Download size={16}/>
              </a>
              <a href={downloadUrl("pdf")} target="_blank" rel="noreferrer" download
                 data-testid="dl-pdf"
                 className="flex items-center gap-3 px-3 py-2.5 rounded border border-[var(--pf-border)] hover:border-[var(--pf-brand)] hover:bg-[var(--pf-surface-2)] transition-colors">
                <FilePdf size={20} weight="duotone" className="text-[var(--pf-brand)]"/>
                <div className="flex-1">
                  <div className="font-bold text-sm">PDF</div>
                  <div className="text-[10px] text-[var(--pf-text-2)]">Print-ready — send to printer</div>
                </div>
                <Download size={16}/>
              </a>
              <a href={downloadUrl("dxf")} target="_blank" rel="noreferrer" download
                 data-testid="dl-dxf"
                 className="flex items-center gap-3 px-3 py-2.5 rounded border border-[var(--pf-border)] hover:border-[var(--pf-brand)] hover:bg-[var(--pf-surface-2)] transition-colors">
                <FileArrowDown size={20} weight="duotone" className="text-[var(--pf-brand)]"/>
                <div className="flex-1">
                  <div className="font-bold text-sm">DXF</div>
                  <div className="text-[10px] text-[var(--pf-text-2)]">For laser cutters / CAD</div>
                </div>
                <Download size={16}/>
              </a>
              <p className="text-[10px] text-[var(--pf-text-3)] pt-2 text-center">
                Free · No account · No watermark
              </p>
            </div>
          </aside>

          {/* --- RIGHT: preview -------------------------------------------- */}
          <main>
            <div className="border border-[var(--pf-border)] rounded-lg bg-white overflow-hidden">
              {/* Tabs */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--pf-border)] bg-[var(--pf-surface-2)]/40">
                <div className="flex gap-1">
                  <button
                    onClick={() => setTab("flat")}
                    data-testid="tab-flat"
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded transition-colors ${tab==="flat" ? "bg-white shadow-sm text-[var(--pf-brand)]" : "text-[var(--pf-text-2)] hover:text-[var(--pf-brand)]"}`}
                  >
                    <StackSimple size={14} weight="bold" className="inline mr-1.5 -mt-0.5"/>Flat Dieline
                  </button>
                  <button
                    onClick={() => setTab("3d")}
                    data-testid="tab-3d"
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded transition-colors ${tab==="3d" ? "bg-white shadow-sm text-[var(--pf-brand)]" : "text-[var(--pf-text-2)] hover:text-[var(--pf-brand)]"}`}
                  >
                    <CubeFocus size={14} weight="bold" className="inline mr-1.5 -mt-0.5"/>3D Fold
                  </button>
                </div>
                <div className="text-xs text-[var(--pf-text-2)]">
                  {preview && (
                    <span data-testid="preview-bbox">
                      Flat sheet: <span className="font-bold text-[var(--pf-text)]">{preview.width_mm.toFixed(0)} × {preview.height_mm.toFixed(0)} mm</span>
                    </span>
                  )}
                  {busy && <span className="ml-3 text-[var(--pf-brand)]">Updating…</span>}
                </div>
              </div>

              {/* Tab content */}
              <div className="p-6 md:p-10 min-h-[520px] flex items-center justify-center bg-[repeating-linear-gradient(45deg,#fafafa_0_10px,#f5f5f5_10px_20px)]">
                {err && (
                  <div className="text-center text-sm text-red-600 max-w-md" data-testid="preview-error">
                    <div className="font-bold mb-1">Can't generate at these dimensions</div>
                    <div>{err}</div>
                  </div>
                )}
                {!err && tab === "flat" && flatSvgWithLogo && (
                  <div
                    className="max-w-full max-h-[600px] drop-shadow-md"
                    data-testid="flat-svg"
                    dangerouslySetInnerHTML={{
                      __html: flatSvgWithLogo.replace(/width="[^"]*"/, 'width="100%"').replace(/height="[^"]*"/, 'height="100%"'),
                    }}
                  />
                )}
                {!err && tab === "3d" && preview && (
                  <Box3D
                    L={preview.dimensions.L || dims.L}
                    W={preview.dimensions.W || dims.W}
                    H={preview.dimensions.H || dims.H}
                    templateSlug={slug}
                    logoDataUrl={logoDataUrl}
                  />
                )}
              </div>
            </div>

            {/* Assembly notes */}
            <div className="mt-6 border border-[var(--pf-border)] rounded-lg p-5 bg-white">
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--pf-brand)] mb-3">
                Assembly notes
              </div>
              <ul className="space-y-2 text-sm text-[var(--pf-text-2)]">
                {(preview?.notes || []).map((n, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-[var(--pf-brand)] font-bold">{i + 1}.</span>
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-4 border-t border-dashed border-[var(--pf-border)] flex flex-wrap gap-4 text-xs text-[var(--pf-text-2)]">
                <div><span className="inline-block w-3 h-0.5 bg-[#e11d48] align-middle mr-1.5"/>Solid red = CUT</div>
                <div><span className="inline-block w-3 h-0.5 border-t border-dashed border-[#2563eb] align-middle mr-1.5"/>Dashed blue = CREASE / FOLD</div>
                <div><span className="font-bold">Template ID:</span> {preview?.template_id}</div>
              </div>
            </div>

            {/* Use cases */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              {template.use_cases.map(u => (
                <div key={u} className="border border-[var(--pf-border)] rounded-lg p-4 bg-white text-center text-xs font-bold uppercase tracking-widest text-[var(--pf-text-2)]">
                  {u}
                </div>
              ))}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
